"use client";

import { getCurrentPath } from "@faire/web--source/common/Path";
import { Pagination } from "@faire/web--source/slate/Pagination";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { trackCategoriesPaginationClick } from "@faire/web-api--source/events/categories/click/pagination";
import { IFilterablePaginatedSection } from "@faire/web-api--source/indigofair/data/IFilterablePaginatedSection";
import { IPaginationData } from "@faire/web-api--source/indigofair/data/IPaginationData";
import React, { useCallback, useContext } from "react";

import { useUpdateQueryParam } from "@faire/retailer/app/(app)/retailer/[locale]/(pagesWithModal)/(defaultHeader)/cart/(treatment)/rsc/_lib/queryParams";
import { logSDUIEvent } from "@faire/retailer/components/SDUI/__internal__/EventLogging/logSDUIEvent";
import {
  ActionLoggingEventParams,
  SectionLoggingEventParams,
} from "@faire/retailer/components/SDUI/__internal__/types";
import { PAGE_QUERY_PARAM_KEY } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/consts";
import { FilterablePaginatedSectionContext } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/FilterablePaginatedSectionContext";
import { SDUIFilterablePaginatedSectionNextPagePrefetcher } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Pagination/SDUIFilterablePaginatedSectionNextPagePrefetcher";
import { paginationRow } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/styles.css";

export const SDUIFilterablePaginatedSectionPaginationRenderer: React.FC<{
  paginationData: IPaginationData | undefined;
  sectionLoggingData: SectionLoggingEventParams;
  filterablePaginatedSection: IFilterablePaginatedSection;
}> = ({ paginationData, sectionLoggingData, filterablePaginatedSection }) => {
  const handlePaginationLink = (pageNumber: number): string => {
    const currentFilterPath = getCurrentPath().delete(PAGE_QUERY_PARAM_KEY);
    if (pageNumber === 1) {
      return currentFilterPath.toString();
    }
    return currentFilterPath.append({ page: pageNumber.toString() }).toString();
  };

  const { scrollToTopFunc, setIsScrollingToTop, startTransition } = useContext(
    FilterablePaginatedSectionContext
  );

  const updateQueryParam = useUpdateQueryParam();
  const handlePageChange = useCallback(
    (queryParamName: string, value?: string) =>
      startTransition?.(() => {
        updateQueryParam(queryParamName, value);
      }),
    [startTransition, updateQueryParam]
  );

  const shouldShowPagination =
    paginationData && (paginationData.page_count ?? 0) > 1;

  if (!shouldShowPagination) {
    return null;
  }

  return (
    <WhenVisibilityIsImminent>
      <div className={paginationRow} data-test-id="sdui-fps-pagination">
        <SDUIFilterablePaginatedSectionNextPagePrefetcher
          filterablePaginatedSection={filterablePaginatedSection}
          paginationData={paginationData}
          handlePaginationLink={handlePaginationLink}
        />
        <Pagination
          paginationData={paginationData}
          pageToLink={handlePaginationLink}
          onChangePage={(page) => {
            logSDUIEvent(
              "CLICK",
              ActionLoggingEventParams.build({
                ...sectionLoggingData,
                action_content: page.toString(),
                action_instance_identifier: "FETCH_PAGE",
              })
            );
            trackCategoriesPaginationClick(
              sectionLoggingData.view_instance_identifier ?? "",
              page.toString()
            );
            scrollToTopFunc?.({ toTop: page === 1 });
            setIsScrollingToTop?.(true);
            if (page === 1) {
              handlePageChange(PAGE_QUERY_PARAM_KEY);
            } else {
              handlePageChange(PAGE_QUERY_PARAM_KEY, page.toString());
            }
          }}
        />
      </div>
    </WhenVisibilityIsImminent>
  );
};
